import {
  PiFire,
  PiWarning,
  PiTrendUp,
  PiClock,
  PiCalendar,
  PiEnvelope,
  PiHourglass,
} from 'react-icons/pi'
import type {
  EnrichedAction,
  SignalType,
} from '../../services/action_summary_api'

/**
 * Configuration for risk level visual styling and icons.
 */
export const RISK_STYLES = {
  at_risk: {
    border: 'border-l-4 border-red-300',
    bg: 'bg-white',
    iconColor: 'text-gray-400',
    Icon: PiWarning,
  },
  opportunity: {
    border: 'border-l-4 border-amber-300',
    bg: 'bg-white',
    iconColor: 'text-gray-400',
    Icon: PiTrendUp,
  },
  routine: {
    border: 'border-l-4 border-gray-200',
    bg: 'bg-white',
    iconColor: 'text-gray-400',
    Icon: PiClock,
  },
} as const

/**
 * Configuration for urgency level icons.
 */
export const URGENCY_ICONS = {
  do_now: PiFire,
  today: PiClock,
  this_week: PiCalendar,
} as const

/**
 * Configuration for urgency level colors.
 */
export const URGENCY_COLORS = {
  do_now: 'text-red-600',
  today: 'text-orange-600',
  this_week: 'text-fb-dark-green',
} as const

/**
 * Tooltip content for risk levels.
 * These explain the AI's assessment of each action's priority.
 */
export const RISK_TOOLTIPS: Record<
  EnrichedAction['risk_level'],
  { title: string; content: string }
> = {
  at_risk: {
    title: 'Needs Attention',
    content:
      'Warning signs detected: long response delays, extended inactivity, or stalled progress. Act quickly to re-engage.',
  },
  opportunity: {
    title: 'Ready to Advance',
    content:
      'Positive momentum: recent engagement, stage progress, or time-sensitive opportunity. Ideal time to move forward.',
  },
  routine: {
    title: 'Follow Up',
    content:
      'Standard maintenance: keep the conversation going and ensure continued progress.',
  },
}

/**
 * Tooltip content for urgency levels.
 */
export const URGENCY_TOOLTIPS: Record<
  EnrichedAction['urgency'],
  { title: string; content: string }
> = {
  do_now: {
    title: 'Do Now',
    content: 'High priority - complete within the next 2 hours',
  },
  today: {
    title: 'Today',
    content: 'Should be completed before end of day',
  },
  this_week: {
    title: 'This Week',
    content: 'Plan to complete within the next few days',
  },
}

/**
 * Number of items to show by default before requiring "Show More" button.
 */
export const DEFAULT_VISIBLE_ITEMS = 3

/**
 * Configuration for signal-based sections displayed in the UI.
 * Ordered by priority: unreplied emails first, then inactive deals.
 */
export const SIGNAL_SECTIONS: Array<{
  key: SignalType
  title: string
  description: string
  Icon: typeof PiEnvelope
  iconColor: string
}> = [
  {
    key: 'unreplied_email',
    title: 'Unreplied Emails',
    description: 'Inbound messages from 3-60 days ago awaiting your response',
    Icon: PiEnvelope,
    iconColor: 'text-gray-500',
  },
  {
    key: 'inactive_deal',
    title: 'Inactive Deals',
    description:
      'Deals needing attention (7-90 days stale or 14+ days in same stage)',
    Icon: PiHourglass,
    iconColor: 'text-gray-500',
  },
] as const

/**
 * Tooltip content for signal types.
 */
export const SIGNAL_TOOLTIPS: Record<
  SignalType,
  { title: string; content: string }
> = {
  unreplied_email: {
    title: 'Unreplied Email',
    content:
      'Inbound emails from 3-60 days ago awaiting your response. Automated emails (shipping, receipts, etc.) are filtered out.',
  },
  inactive_deal: {
    title: 'Inactive Deal',
    content:
      'Deals with no activity in 7+ days OR stuck in the same stage for 14+ days. Limited to deals active within the last 90 days.',
  },
}

/**
 * Icon colors for each signal type.
 * Used consistently across signal badges and company sections.
 */
export const SIGNAL_ICON_COLORS: Record<SignalType, string> = {
  unreplied_email: 'text-gray-500',
  inactive_deal: 'text-gray-500',
}

/**
 * Format a signal-specific label showing the days metric.
 *
 * @param signalType - The type of signal
 * @param daysUnreplied - Days since email received (for unreplied_email)
 * @param daysStale - Days since deal activity (for inactive_deal)
 * @param daysInStage - Days in current stage (for inactive_deal)
 * @param currentStage - Current sales stage name
 * @returns Formatted label string (e.g., "5d unreplied", "12d inactive")
 */
export const formatSignalLabel = (
  signalType: SignalType,
  daysUnreplied: number | null,
  daysStale: number | null,
  daysInStage: number | null,
  currentStage: string | null
): string => {
  switch (signalType) {
    case 'unreplied_email':
      return `${daysUnreplied ?? 0}d unreplied`
    case 'inactive_deal': {
      // Show the most relevant metric
      if (daysInStage && daysInStage >= 14) {
        return `${daysInStage}d in ${currentStage || 'stage'}`
      }
      return `${daysStale ?? 0}d inactive`
    }
    default:
      return ''
  }
}
